import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { ChildTicketSheetDropdown } from './ChildTicketSheetDropdown.js';
import type { ChildTicketRow } from '@cortex/domain-product-innovation-lab-basic-design-tree';
import { readCell, type SheetValueColumn } from './child-ticket-sheet.js';

/** 列ごとの候補（項目 / リポジトリ / 担当者）。 */
export interface CellOptions {
  /** 「項目」の候補。 */
  item: readonly { value: string; label: string }[];
  /** 「リポジトリ」の候補。 */
  repository: readonly { value: string; label: string }[];
  /** 「担当者」の候補。 */
  assignee: readonly { value: string; label: string }[];
}

/**
 * 文字を打ち込む列の編集。打ち始めた 1 文字目をそのまま初期値にする
 * （表計算と同じで、セルを選んで打てば上書きが始まる）。
 * @graph-connects none
 */
function TextCell({
  value,
  multiline,
  ariaLabel,
  onCommit,
  onCancel,
}: {
  value: string;
  multiline: boolean;
  ariaLabel: string;
  onCommit: (next: string) => void;
  onCancel: () => void;
}): ReactNode {
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  const className =
    'w-full rounded-md border border-[var(--color-border-focus)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm text-[var(--color-text-primary)] focus:outline-none';
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    // セル内の編集なので、表側の矢印移動へは渡さない
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onCommit((e.currentTarget as HTMLTextAreaElement | HTMLInputElement).value);
    }
  };
  if (multiline) {
    return (
      <textarea
        ref={ref as React.RefObject<HTMLTextAreaElement>}
        rows={2}
        defaultValue={value}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          onCommit(e.target.value);
        }}
        className={`${className} resize-none leading-5`}
      />
    );
  }
  return (
    <input
      ref={ref as React.RefObject<HTMLInputElement>}
      type="text"
      inputMode="decimal"
      defaultValue={value}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      onBlur={(e) => {
        onCommit(e.target.value);
      }}
      className={`${className} text-right font-mono tabular-nums`}
    />
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 基本設計シートのセル 1 つ。選択中は値を文字で見せるだけにし、編集を始めたときだけ入力欄や候補リストを出す（表計算と同じで、矢印キーの移動が入力に吸われない）
 * @graph-connects ChildTicketSheetDropdown [calls] 候補から選ぶ列の編集
 * @graph-connects TextCell [calls] 文字を打ち込む列の編集
 * @graph-connects readCell [calls] セルの文字列表現
 */
export function ChildTicketSheetCell({
  row,
  column,
  editing,
  seed,
  readOnlyValue,
  options,
  ariaLabel,
  onCommit,
  onCancel,
}: {
  /** 描く行。 */
  row: ChildTicketRow;
  /** 描く列（種別はメニュー専用なので来ない）。 */
  column: SheetValueColumn;
  /** 編集中かどうか。 */
  editing: boolean;
  /** 打ち始めの 1 文字（無ければ null）。 */
  seed: string | null;
  /** 自動計算で決まった表示値（pt 列のみ。無ければ null）。 */
  readOnlyValue: string | null;
  /** 候補一覧。 */
  options: CellOptions;
  /** 読み上げ用のセル名。 */
  ariaLabel: string;
  /** 確定ハンドラ。 */
  onCommit: (next: string) => void;
  /** 取り消しハンドラ。 */
  onCancel: () => void;
}): ReactNode {
  const value = readCell(row, column);
  // 候補リストはセルの外（portal）に出すので、基準になる要素をここで持つ
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  if (readOnlyValue !== null) {
    // 自動計算の pt。打てる数字と同じ見え方にすると入力を誘うので、値を灰の
    // 小片に収めて「導かれた数字」として見せる。
    return (
      <span className="block text-right">
        <span className="child-sheet-auto-point font-mono text-sm tabular-nums">
          {readOnlyValue}
        </span>
      </span>
    );
  }

  if (editing) {
    if (column === 'content' || column === 'point') {
      return (
        <TextCell
          value={seed ?? value}
          multiline={column === 'content'}
          ariaLabel={ariaLabel}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      );
    }
    return (
      <>
        <span ref={anchorRef} className="block text-sm text-[var(--color-text-primary)]">
          {value === '' ? '—' : value}
        </span>
        <ChildTicketSheetDropdown
          options={options[column]}
          value={value === '' ? null : value}
          ariaLabel={ariaLabel}
          anchorRef={anchorRef}
          onCommit={(next) => {
            onCommit(next ?? '');
          }}
          onCancel={onCancel}
        />
      </>
    );
  }

  if (value === '') {
    return <span className="block text-sm text-[var(--color-text-disabled)]">—</span>;
  }
  if (column === 'point') {
    return (
      <span className="block text-right font-mono text-sm tabular-nums text-[var(--color-text-primary)]">
        {value}
      </span>
    );
  }
  return (
    <span className="block text-sm break-words text-[var(--color-text-primary)]">{value}</span>
  );
}
